"use-client";
import {
  Box,
  chakra,
  Checkbox,
  Flex,
  Icon,
  Table,
  TableBodyProps,
  TableCellProps,
  TableColumnHeaderProps,
  TableContainer,
  TableHeadProps,
  TableProps,
  TableRowProps,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  Spacer,
  Center,
  Button,
  Tfoot,
} from "@chakra-ui/react";
import {
  CellContext,
  ColumnDef,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  RowData,
  RowSelectionState,
  SortingState,
  TableOptions,
  Updater,
  useReactTable,
} from "@tanstack/react-table";
import { isBoolean, isEmpty, isFunction, isNumber } from "lodash";
import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
} from "react";
import { AiFillCheckCircle, AiFillCloseCircle } from "react-icons/ai";
import { VscTriangleDown, VscTriangleUp } from "react-icons/vsc";
import { Paginate } from "../Paginate";
import {
  EmptyResultState,
  EmptyResultStateProps,
  LoadingDataTable,
} from "./DataTableState";
import { formatWithComma } from "@picovico/utils";
import { Params } from "custom-types";

declare module "@tanstack/table-core" {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  interface ColumnMeta<TData extends RowData, TValue> {
    isNumeric?: boolean;
    isCustomCell?: boolean;
    currency?: string;
    tHeadProps?: TableColumnHeaderProps;
    // Plain-language explanation of the column, shown when its header is hovered.
    // Rendered by admin's MinBaseListing; DataTable does not show it.
    tooltip?: string;
    tBodyProps?: TableBodyProps;
    tRowProps?: TableRowProps;
    tDataProps?: TableCellProps;
  }
}

export interface DataTableBaseProps<TData>
  extends Pick<
    TableOptions<TData>,
    "enableRowSelection" | "enableMultiRowSelection"
  > {
  data: TData[];
  pageCount?: number;
  totalRecords?: number;
  recordRange?: number[];
  handlePageChange?: (page: number) => void;
  error?: string;
  filterAction?: React.ReactNode;
  activePage?: number;
  indexStart?: number;
  isError?: boolean;
  columnSchema: ColumnDef<TData>[];
  TableActions?: React.FC<CellContext<TData, unknown>>;
  isLoading?: boolean;
  emptyOptions?: EmptyResultStateProps;
  enableRowSelection?: boolean;
  onRowClick?: (arg: TData) => void;
  onRowSelectionChange?: (selectedRowIndex: Record<number, boolean>) => void;
  ref?: React.RefObject<{ resetTable: () => void }>;
  currentPage?: number;
  handleLoadMore?: (pageSize: number) => void;
  isFetching?: boolean;
  qParams?: Params;
  showLoadMore?: boolean;
  setVisibleColumns?: (value: {
    visible: (string | undefined)[];
    hidden: (string | undefined)[];
    all: (string | undefined)[];
  }) => void;
}

export interface DataTableExtraProps {
  tableContainerProps?: TableProps;
  tableProps?: TableProps;
  tHeadProps?: TableHeadProps;
  tBodyProps?: TableBodyProps;
  tRowProps?: TableRowProps;
  tCellProps?: TableCellProps;
  tHeadCellProps?: TableCellProps;
  tFootCellProps?: TableCellProps;
  tFootRowProps?: TableRowProps;
}

export type DataTableProps<TData> = DataTableBaseProps<TData> &
  DataTableExtraProps;

/** @type {D} - RowData Type*/
function DataTable<TData>(
  {
    data,
    totalRecords = 0,
    recordRange,
    columnSchema,
    tableContainerProps,
    tableProps,
    tHeadProps,
    tBodyProps,
    tRowProps,
    tCellProps,
    tHeadCellProps,
    pageCount,
    TableActions,
    activePage,
    handlePageChange,
    enableRowSelection,
    enableMultiRowSelection,
    onRowSelectionChange,
    isLoading,
    onRowClick,
    isError,
    emptyOptions,
    showLoadMore = false,
    currentPage,
    handleLoadMore,
    isFetching = false,
    qParams,
    setVisibleColumns,
    tFootRowProps,
    tFootCellProps,
  }: DataTableProps<TData>,
  ref: React.Ref<{ resetTable: () => void }>,
) {
  const [sorting, setSorting] = React.useState<SortingState>([]);
  const [rowSelection, setRowSelection] = React.useState({});
  const defaultData = useMemo(() => [], []);
  const tableColumns = useMemo(() => {
    // A copy: the select and actions columns are added below, and doing that
    // to the caller's array left a stale actions column (closing over an
    // earlier render's props) behind for the next mount of the page.
    const schema: ColumnDef<TData>[] = [...columnSchema];
    if (schema.length === 0) return columnSchema;

    if (
      (enableMultiRowSelection || enableRowSelection) &&
      schema[0]?.id !== "select"
    ) {
      schema.unshift({
        id: "select",
        enableSorting: false,
        header: ({ table }) => (
          <Checkbox
            color="primary"
            {...{
              isChecked: table.getIsAllRowsSelected(),
              isIndeterminate: table.getIsSomeRowsSelected(),
              onChange: table.getToggleAllRowsSelectedHandler(),
            }}
          />
        ),
        cell: ({ row }) => (
          <Box
            as="div"
            onClick={(e) => {
              e.stopPropagation();
            }}
          >
            <Checkbox
              as="div"
              color="primary"
              {...{
                isChecked: row.getIsSelected(),
                isDisabled: !row.getCanSelect(),
                isIndeterminate: row.getIsSomeSelected(),
                id: `checkbox-${row.id}`,
                onChange: (e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  row.getToggleSelectedHandler()(e);
                },
              }}
            />
          </Box>
        ),
      });
    }

    if (TableActions && schema[schema.length - 1]?.id !== "actions") {
      schema.push({
        id: "actions",
        header: "Actions",
        cell: (props) => <TableActions {...props} />,
      });
    }

    return schema;
  }, [columnSchema, TableActions, enableMultiRowSelection, enableRowSelection]);

  const table = useReactTable({
    columns: tableColumns,
    data: data ?? defaultData,
    enableRowSelection,
    enableMultiRowSelection,
    onRowSelectionChange: (updaterOrValue: Updater<RowSelectionState>) => {
      setRowSelection(updaterOrValue);
      if (onRowSelectionChange) {
        const newValue = isFunction(updaterOrValue)
          ? updaterOrValue(rowSelection)
          : {
              ...rowSelection,
              ...updaterOrValue,
            };
        onRowSelectionChange(newValue);
      }
    },
    getCoreRowModel: getCoreRowModel(),
    onSortingChange: setSorting,
    getSortedRowModel: getSortedRowModel(),
    manualPagination: true,
    pageCount: pageCount ?? 1,
    state: {
      sorting,
      rowSelection,
    },
  });

  const shouldShowFooter = useMemo(
    () =>
      table
        .getFooterGroups()
        .some((group) =>
          group.headers.some(
            (header) => header.column.columnDef.footer && !header.isPlaceholder,
          ),
        ),
    [table],
  );

  useEffect(() => {
    const visibleColumns = table
      .getVisibleFlatColumns()
      .map((col: ColumnDef<TData>) => col.id);
    const allColumns = table
      .getAllFlatColumns()
      .map((col: ColumnDef<TData>) => col.id);
    const hiddenColumns = allColumns.filter(
      (col) => !visibleColumns.includes(col),
    );
    if (setVisibleColumns) {
      setVisibleColumns({
        visible: visibleColumns,
        hidden: hiddenColumns,
        all: allColumns,
      });
    }
  }, [table]);

  useImperativeHandle(ref, () => ({
    resetTable: () => {
      table.resetRowSelection();
    },
  }));

  return (
    <>
      <TableContainer
        w={"100%"}
        maxH="100%"
        overflowY="auto"
        {...tableContainerProps}
      >
        <Table data-testid="data-table" variant="sageStriped" {...tableProps}>
          <Thead
            position="sticky"
            top={0}
            backgroundColor={"white"}
            zIndex={1}
            {...tHeadProps}
          >
            {table?.getHeaderGroups().map((headerGroup) => (
              <Tr key={headerGroup.id} {...tRowProps}>
                {headerGroup.headers.map((header) => {
                  const meta = header.column.columnDef.meta;
                  return (
                    <Th
                      key={header.id}
                      {...(header.colSpan > 1 && { colSpan: header.colSpan })}
                      {...meta?.tHeadProps}
                      isNumeric={meta?.isNumeric}
                      onClick={header.column.getToggleSortingHandler()}
                      {...tHeadCellProps}
                      sx={{ textAlign: meta?.isNumeric ? 'right' : 'left', ...meta?.tHeadProps?.sx }}
                    >
                      {!header.isPlaceholder &&
                        flexRender(
                          header.column.columnDef.header,
                          header.getContext(),
                        )}
                      <chakra.span
                        pl="1"
                        sx={{
                          cursor: "pointer",
                        }}
                      >
                        {!header.isPlaceholder &&
                          header.column.getIsSorted() &&
                          (header.column.getIsSorted() === "desc" ? (
                            <Icon
                              as={VscTriangleUp}
                              aria-label="sorted descending"
                            />
                          ) : (
                            <Icon
                              as={VscTriangleDown}
                              aria-label="sorted ascending"
                            />
                          ))}
                      </chakra.span>
                    </Th>
                  );
                })}
              </Tr>
            ))}
          </Thead>
          {isLoading ? (
            <LoadingDataTable noOfCols={tableColumns.length} />
          ) : !isError && isEmpty(data) ? (
            <EmptyResultState
              noOfCols={tableColumns.length}
              {...emptyOptions}
            />
          ) : (
            <>
              <Tbody
                data-testid="data-table-body"
                {...tBodyProps}
                overflow="scroll"
              >
                {table.getRowModel().rows.map((row) => (
                  <Tr
                    key={row.id}
                    cursor={"pointer"}
                    {...tRowProps}
                    onClick={(e) => {
                      onRowClick && onRowClick(row.original);
                      const target = e.target as HTMLElement;
                      if (target.id && target.id.startsWith(`checkbox-`))
                        return;
                    }}
                  >
                    {row.getVisibleCells().map((cell) => {
                      const meta = cell.column.columnDef.meta;
                      return (
                        <Td
                          key={cell.id}
                          isNumeric={meta?.isNumeric}
                          {...meta?.tDataProps}
                          {...tCellProps}
                          textAlign={
                            isNumber(cell.getValue()) ? "end" : "start"
                          }
                        >
                          {isBoolean(cell.getValue()) && !meta?.isCustomCell ? (
                            <Icon
                              data-testid="boolean-icon"
                              as={
                                cell.getValue()
                                  ? AiFillCheckCircle
                                  : AiFillCloseCircle
                              }
                              color={cell.getValue() ? "green.500" : "red.500"}
                              aria-label={cell.getValue() ? "true" : "false"}
                              fontSize="2xl"
                            />
                          ) : !cell.getValue() &&
                            cell.getValue() !== 0 &&
                            cell.column.id !== "actions" ? (
                            "N/A"
                          ) : isNumber(cell.getValue()) &&
                            !meta?.isCustomCell ? (
                            `${meta?.currency ? meta.currency : ""} ${formatWithComma(cell.getValue() as number)}`
                          ) : (
                            flexRender(
                              cell.column.columnDef.cell,
                              cell.getContext(),
                            )
                          )}
                        </Td>
                      );
                    })}
                  </Tr>
                ))}
              </Tbody>
              <Tfoot>
                {shouldShowFooter &&
                  table
                    .getFooterGroups()
                    .filter((group) => group.depth === 1)
                    ?.map((footerGroup) => (
                      <Tr key={footerGroup.id} {...tFootRowProps}>
                        {footerGroup.headers.map((header) =>
                          header?.isPlaceholder ? null : (
                            <Td key={header.id} {...tFootCellProps}>
                              {flexRender(
                                header.column.columnDef.footer,
                                header.getContext(),
                              )}
                            </Td>
                          ),
                        )}
                      </Tr>
                    ))}
              </Tfoot>
            </>
          )}
        </Table>
      </TableContainer>
      {showLoadMore && handleLoadMore
        ? pageCount &&
          currentPage &&
          pageCount > currentPage && (
            <Flex w="full" my="2" justifyContent={"flex-end"}>
              <Button
                size={"sm"}
                variant={"outline"}
                onClick={() => handleLoadMore(Number(qParams?.page_size || 30))}
                isLoading={isFetching}
              >
                Load More
              </Button>
            </Flex>
          )
        : pageCount &&
          totalRecords > 0 &&
          handlePageChange && (
            <Flex w="full" my="2">
              <Center color={"gray.500"}>
                {recordRange &&
                  totalRecords > 0 &&
                  `Showing ${recordRange?.[0]} to ${recordRange?.[1]} of ${totalRecords} entries`}
              </Center>
              <Spacer />
              <Paginate
                activePage={activePage}
                pageCount={table.getPageCount()}
                onPageChange={handlePageChange}
              />
            </Flex>
          )}
    </>
  );
}

export default React.memo(forwardRef(DataTable)) as typeof DataTable;
